import { assign, find, kebabCase, map } from 'lodash';
import valueFilters from '../valueFilters';
import validator from '../validator';
import modelFactory, { IModel } from '../modelFactory';
import contactModel, { IContact } from './contactModel';
import addressModel, { IAddress } from './addressModel';
import phonable, { IPhonable } from './phonable';
import model from './model';
import enums from '../enum';
import productModel, { IProduct } from './productModel';
import vendorAdditionalInfoModel, { IVendorAdditionalInfo } from './vendorAdditionalInfoModel';
import vendorCostFactorModel, { IVendorCostFactor } from './vendorCostFactor';
import { IModelSet } from 'Scripts/modelSet';
import additionalInfoContainer, { IAdditionalInfoContainer } from './additionalInfoContainer';

export interface IVendor extends IModel, IPhonable, IAdditionalInfoContainer<IVendorAdditionalInfo> {
	type?: number;
	name?: string;
	freightFactor: number;
	overheadFactorBuilder: number;
	overheadFactorRetail: number;
	officeAddress: IAddress;
	contacts?: IModelSet<IContact>;
	costFactors?: IVendorCostFactor[];
	addresses?: IModelSet<IAddress>;
	// additionalInfo: IModelSet<IVendorAdditionalInfo>
	primaryContact?: IContact;
	products?: IModelSet<IProduct>;
	getProductBySku(sku: string): IProduct;
}

const vendorFactory = modelFactory.createFactory<IVendor>('Vendor', assign({}, model, phonable, additionalInfoContainer, {
	type: {
		setFilters: valueFilters.toNumber,
		rules: validator.enum('vendorTypes', true) // disallow unknown
	},
	name: {
		rules: validator.required()
	},
	contacts: {
		setFilters: valueFilters.toModelSet(contactModel),
		rules: validator.validModel(contactModel.rules())
	},
	addresses: {
		setFilters: valueFilters.toModelSet(addressModel),
		rules: validator.validModel(addressModel.rules())
	},
	additionalInfo: {
		defaultValue: [],
		setFilters: valueFilters.toModelSet(vendorAdditionalInfoModel),
		rules: validator.validModel(vendorAdditionalInfoModel.rules())
	},
	primaryContact: {
		nonSerialized: true
	},
	_get_primaryContact: function () {
		return this._getContactWithPriority(0);
	},
	_set_primaryContact: function (val) {
		this.setContactWithPriority(val, 0);
	},
	primaryContactName: {
		writable: false,
		nonSerialized: true
	},
	_get_primaryContactName: function () {
		return this.primaryContact ? this.primaryContact.fullName : '';
	},
	setContactWithPriority: function (contact, priority) {
		if (!this.contacts) {
			this.contacts = [];
		}

		const existing = find(this.contacts, c => c.priority === priority && !c.externalFormKey);

		if (existing) {
			if (!contact) {
				this.contacts.delete(existing.key);
			}
			else {
				assign(existing, contact, { priority: priority });
			}
		}
		else if (contact) {
			contact.priority = priority;
			this.contacts[contact.key] = contact;
		}
	},
	_getContactWithPriority: function (priority) {
		return find(this.contacts, c => c.priority === priority && !c.externalFormKey);
	},
	getKebabName: function () {
		return kebabCase(this.name);
	},
	products: {
		setFilters: valueFilters.toModelSet(productModel),
		rules: validator.validModel(productModel.rules())
	},
	getProductByName: function (name) {
		return find(this.products, { name: name });
	},
	getProductBySku: function (sku) {
		return find(this.products, { sku: sku });
	},
	officeAddress: {
		nonSerialized: true
	},
	_set_officeAddress: function (val) {
		this.setAddressOfType(val, enums.addressTypes.Office);
	},
	_get_officeAddress: function () {
		const result = this.getAddressByType(enums.addressTypes.Office);

		return result ? result : undefined;
	},
	setAddressOfType: function (address, type) {
		if (!this.addresss) {
			this.addresses = [];
		}

		const existing = find(this.addresses, x => { return enums.addressTypes.isSet(type, x.type); });

		if (existing) {
			if (!address) {
				this.addresses.delete(existing.key);
			}
			else {
				assign(existing, address, { type: enums.addressTypes.set(type, address.type) });
			}
		}
		else if (address) {
			address.type = enums.addressTypes.set(type, address.type);
			this.addresses[address.key] = address;
		}
	},
	getAddressByType: function (type) {
		return find(this.addresses, x => {
			return enums.addressTypes.isSet(type, x.type);
		});
	},
	freightFactor: {
		nonSerialized: true,
		setFilters: valueFilters.toNumber
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the freightFactor property
	_get_freightFactor: function () {
		const stringVal = this.getAdditionalInfoValue('freightFactor', '0');
		return Number(stringVal);
	},
	_set_freightFactor: function (val) {
		this.setAdditionalInfo('freightFactor', val);
	},
	overheadFactorBuilder: {
		nonSerialized: true,
		setFilters: valueFilters.toNumber
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the overheadFactorBuilder property
	_get_overheadFactorBuilder: function () {
		const stringVal = this.getAdditionalInfoValue('overheadFactorBuilder', '0');
		return Number(stringVal);
	},
	_set_overheadFactorBuilder: function (val) {
		this.setAdditionalInfo('overheadFactorBuilder', val);
	},
	overheadFactorRetail: {
		nonSerialized: true,
		setFilters: valueFilters.toNumber
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the overheadFactorRetail property
	_get_overheadFactorRetail: function () {
		const stringVal = this.getAdditionalInfoValue('overheadFactorRetail', '0');
		return Number(stringVal);
	},
	_set_overheadFactorRetail: function (val) {
		this.setAdditionalInfo('overheadFactorRetail', val);
	},
	costFactors: {
		nonSerialized: true,
		writable: false,
		getFilters: function (val) {
			if (!val) {
				// set the backing value and return the newly set value
				val = this._costFactors = map(JSON.parse(this.costFactorJson), f => vendorCostFactorModel(f));
			}

			return val;
		}
	},
	costFactorJson: {
		nonSerialized: true,
		setFilters: valueFilters.toString
	},
	// NOTE: (DR) do not use the getter and setter functions, rather use the costFactorJson property
	_get_costFactorJson: function () {
		const stringVal = this.getAdditionalInfoValue('costFactorJson', '[]');
		return stringVal;
	},
	_set_costFactorJson: function (val) {
		this.setAdditionalInfo('costFactorJson', val);
		this._costFactors = undefined; // force a reparse on next read of costFactors
	},
	// getAdditionalInfo: function (name) {
	// 	if (!this.additionalInfo) { return; }

	// 	return find(this.additionalInfo, { name });
	// },
	// getAdditionalInfoValue: function (name, defaultValue = '') {
	// 	const info = this.getAdditionalInfo(name);

	// 	return info && info.value || defaultValue;
	// },
	// setAdditionalInfo: function (name, value) {
	// 	if (!this.additionalInfo) { return; }

	// 	const isUndefined = typeof value === 'undefined' || value === '';
	// 	let result = this.getAdditionalInfo(name);

	// 	if (!result) {
	// 		result = this.additionalInfo.add({ name });
	// 	}

	// 	if (result) {
	// 		if (isUndefined) {
	// 			this.additionalInfo.delete(result.key);
	// 			result = undefined;
	// 		}
	// 		else {
	// 			result.value = value;
	// 		}
	// 	}

	// 	return result;
	// }
}));

export default vendorFactory;